import {
	Box,
	Chip,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Info, Pencil } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getTrendChipProps } from "web_myplann/Portfolio/Helpers/getTrendChipProps";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type Props = {
	value: number;
	valueLabel: string;
	currencySign: string;
	evaluationPercent?: number | null;
	valueSource?: string;
	showEditValue: boolean;
	onEditValue?: () => void;
	onShowHistory?: () => void;
};

export const DetailHeroValueBox: FC<Props> = ({
	value,
	valueLabel,
	currencySign,
	evaluationPercent,
	valueSource,
	showEditValue,
	onEditValue,
	onShowHistory,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const trend = getTrendChipProps(evaluationPercent);

	return (
		<Box
			mt={theme.spacingTokens.sm}
			bgcolor={theme.palette.grey[50]}
			borderRadius={theme.radii.lg}
			px={theme.spacingTokens.lg}
			py={theme.spacingTokens.md}
		>
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				gap={theme.spacingTokens.md}
			>
				<Stack minWidth={0}>
					<Stack
						direction="row"
						alignItems="center"
						gap={theme.spacingTokens.xs}
					>
						<Typography variant="overline" color={theme.palette.grey[400]}>
							{valueLabel}
						</Typography>
						{onShowHistory && (
							<IconButton
								onClick={onShowHistory}
								aria-label={t(
									Resources.PortfolioDetail.Property.MarketPriceHistory,
								)}
								sx={{ p: 0 }}
							>
								<Info
									size={theme.iconSize.xs}
									color={theme.palette.grey[400]}
								/>
							</IconButton>
						)}
					</Stack>
					<Stack
						direction="row"
						alignItems="baseline"
						gap={theme.spacingTokens.sm}
						mt={theme.spacingTokens.xs}
					>
						<Typography variant="h1" tabular>
							{moneyFormatter(value, currencySign, "", 0)}
						</Typography>
						{showEditValue && (
							<IconButton
								onClick={onEditValue}
								aria-label={t(
									Resources.PortfolioDetail.Property.ValueDrawer.updateTitle,
								)}
								sx={{ p: 0, flexShrink: 0 }}
							>
								<Pencil
									size={theme.iconSize.sm}
									color={theme.palette.blue[500]}
								/>
							</IconButton>
						)}
					</Stack>
				</Stack>

				{trend !== null && (
					<Chip
						variant="trend"
						color={trend.color}
						icon={<trend.Icon size={theme.iconSize.sm} />}
						label={trend.label}
					/>
				)}
			</Stack>

			{valueSource && (
				<Typography
					variant="caption"
					color={theme.palette.grey[400]}
					tabular
					mt={theme.spacingTokens.xs}
				>
					{valueSource}
				</Typography>
			)}
		</Box>
	);
};
